import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { montantEnLettres, SOCIETE, type Facture } from '@sirajoptic/shared';
import { date, nombre } from '@/lib/format';

/**
 * Facture PDF — reprise fidèle du papier en usage au magasin.
 *
 * Exception assumée à la charte de l'application : ce document **garde sa
 * charte imprimée** (barres vertes, mise en page existante). Le thème
 * moutarde / noir / blanc ne s'applique qu'à l'interface.
 */

const VERT = SOCIETE.couleur;
const NOIR = '#111111';
const GRIS = '#555555';

type Vfs = Record<string, string>;

/**
 * Reconnaît la table des polices à son contenu plutôt qu'à son emplacement.
 *
 * `vfs_fonts` a changé de forme au fil des versions de pdfmake : tantôt
 * `{ pdfMake: { vfs } }`, tantôt `{ vfs }`, et en 0.2.x le module **est** la
 * table (`module.exports = vfs`). Chercher une clé nommée `vfs` échouait donc
 * silencieusement sur cette dernière forme, et le PDF partait sans police.
 * Un objet dont les clés sont des `.ttf` est la table, où qu'il se trouve.
 */
function estVfs(valeur: unknown): valeur is Vfs {
  if (typeof valeur !== 'object' || valeur === null) return false;
  return Object.keys(valeur).some((k) => k.endsWith('.ttf'));
}

function trouverVfs(polices: unknown): Vfs | null {
  const m = polices as {
    default?: { pdfMake?: { vfs?: unknown }; vfs?: unknown };
    pdfMake?: { vfs?: unknown };
    vfs?: unknown;
  };

  for (const candidat of [
    m.default?.pdfMake?.vfs,
    m.default?.vfs,
    m.pdfMake?.vfs,
    m.vfs,
    // 0.2.x : le module exporte directement la table.
    m.default,
    polices,
  ]) {
    if (estVfs(candidat)) return candidat;
  }
  return null;
}

/** pdfmake est lourd : on ne le charge qu'au moment où l'on imprime. */
async function chargerPdfMake() {
  const [{ default: pdfMake }, polices] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);

  const vfs = trouverVfs(polices);
  if (!vfs) {
    /* Mieux vaut le dire que produire un document muet : sans police, pdfmake
     * échoue plus loin sur un message qui ne désigne pas la cause. */
    throw new Error(
      "Les polices du PDF n'ont pas pu être chargées. Rechargez la page ; si cela persiste, la dépendance pdfmake est à vérifier.",
    );
  }

  const avecApi = pdfMake as unknown as { addVirtualFileSystem?: (v: Vfs) => void; vfs?: Vfs };
  // 0.2.x expose une méthode dédiée ; les versions antérieures veulent l'affectation.
  if (typeof avecApi.addVirtualFileSystem === 'function') avecApi.addVirtualFileSystem(vfs);
  else avecApi.vfs = vfs;

  return pdfMake;
}

/** Barre verte pleine largeur, motif du papier à en-tête. */
function barre(hauteur: number, marges: [number, number, number, number]): Content {
  return {
    canvas: [{ type: 'rect', x: 0, y: 0, w: 515, h: hauteur, color: VERT }],
    margin: marges,
  };
}

/** Une case d'ordonnance : vision de loin ou vision de près. */
function caseOrdonnance(
  titre: string,
  valeurs: {
    od_sph: string | null;
    od_cyl: string | null;
    og_sph: string | null;
    og_cyl: string | null;
  },
): Content {
  const cellule = (v: string | null) => ({
    text: v ?? '',
    alignment: 'center' as const,
    fontSize: 9,
  });

  return {
    table: {
      widths: ['*', '*', '*'],
      body: [
        [
          {
            text: titre,
            colSpan: 3,
            alignment: 'center',
            bold: true,
            fontSize: 9,
            fillColor: '#EFEFEF',
            color: NOIR,
          },
          {},
          {},
        ],
        [
          { text: '', fontSize: 9 },
          { text: 'SPH', alignment: 'center', bold: true, fontSize: 8, color: GRIS },
          { text: 'CYL', alignment: 'center', bold: true, fontSize: 8, color: GRIS },
        ],
        [
          { text: 'O.D', bold: true, fontSize: 9 },
          cellule(valeurs.od_sph),
          cellule(valeurs.od_cyl),
        ],
        [
          { text: 'O.G', bold: true, fontSize: 9 },
          cellule(valeurs.og_sph),
          cellule(valeurs.og_cyl),
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#999999',
      vLineColor: () => '#999999',
    },
  };
}

export function construireDocument(facture: Facture): TDocumentDefinitions {
  const lignes = facture.lignes.map((l) => [
    { text: String(l.quantite), alignment: 'center' as const, fontSize: 9 },
    { text: l.designation, fontSize: 9 },
    { text: nombre(l.prix_unitaire_ht), alignment: 'right' as const, fontSize: 9 },
    { text: nombre(l.total_ligne_ht), alignment: 'right' as const, fontSize: 9 },
  ]);

  // Quelques lignes vides pour retrouver la hauteur du bloc imprimé.
  while (lignes.length < 6) {
    lignes.push([
      { text: ' ', alignment: 'center', fontSize: 9 },
      { text: ' ', fontSize: 9 },
      { text: ' ', alignment: 'right', fontSize: 9 },
      { text: ' ', alignment: 'right', fontSize: 9 },
    ]);
  }

  const ttc = facture.total_ttc ?? 0;
  const enLettres = facture.montant_lettres ?? montantEnLettres(ttc);

  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 90],

    content: [
      barre(6, [0, 0, 0, 0]),
      {
        text: SOCIETE.nom,
        fontSize: 26,
        bold: true,
        color: VERT,
        alignment: 'center',
        margin: [0, 8, 0, 4],
      },
      {
        text: 'Opticien — Lunetterie',
        fontSize: 9,
        color: GRIS,
        alignment: 'center',
        margin: [0, 0, 0, 6],
      },
      barre(3, [0, 0, 0, 18]),

      {
        columns: [
          { text: '', width: '*' },
          {
            width: 'auto',
            text: `${facture.ville} le ${date(facture.date_facture)}`,
            fontSize: 10,
          },
        ],
        margin: [0, 0, 0, 12],
      },

      {
        text: `Facture N° ${facture.reference}`,
        fontSize: 13,
        bold: true,
        margin: [0, 0, 0, 6],
      },
      { text: `M : ${facture.client_nom}`, fontSize: 11, margin: [0, 0, 0, 2] },
      ...(facture.client_ice
        ? [{ text: `ICE : ${facture.client_ice}`, fontSize: 9, color: GRIS }]
        : []),

      {
        columns: [
          caseOrdonnance('VISION DE LOIN', {
            od_sph: facture.od_loin_sph,
            od_cyl: facture.od_loin_cyl,
            og_sph: facture.og_loin_sph,
            og_cyl: facture.og_loin_cyl,
          }),
          { text: '', width: 20 },
          caseOrdonnance('VISION DE PRÈS', {
            od_sph: facture.od_pres_sph,
            od_cyl: facture.od_pres_cyl,
            og_sph: facture.og_pres_sph,
            og_cyl: facture.og_pres_cyl,
          }),
        ],
        margin: [0, 14, 0, 18],
      },

      {
        table: {
          headerRows: 1,
          widths: [40, '*', 80, 80],
          body: [
            [
              { text: 'Qté', alignment: 'center', bold: true, fontSize: 9, fillColor: '#EFEFEF' },
              { text: 'Désignation', bold: true, fontSize: 9, fillColor: '#EFEFEF' },
              {
                text: 'Prix HT',
                alignment: 'right',
                bold: true,
                fontSize: 9,
                fillColor: '#EFEFEF',
              },
              {
                text: 'Montant HT',
                alignment: 'right',
                bold: true,
                fontSize: 9,
                fillColor: '#EFEFEF',
              },
            ],
            ...lignes,
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#999999',
          vLineColor: () => '#999999',
        },
      },

      {
        columns: [
          { text: '', width: '*' },
          {
            width: 200,
            table: {
              widths: ['*', 'auto'],
              body: [
                [
                  { text: 'Total HT', fontSize: 9 },
                  { text: `${nombre(facture.total_ht)} DH`, alignment: 'right', fontSize: 9 },
                ],
                [
                  { text: 'TVA 20%', fontSize: 9 },
                  { text: `${nombre(facture.total_tva)} DH`, alignment: 'right', fontSize: 9 },
                ],
                [
                  { text: 'Total TTC', bold: true, fontSize: 10 },
                  {
                    text: `${nombre(ttc)} DH`,
                    alignment: 'right',
                    bold: true,
                    fontSize: 10,
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#999999',
              vLineColor: () => '#999999',
            },
          },
        ],
        margin: [0, 14, 0, 16],
      },

      {
        text: `Arrêtée la présente facture à la somme de : ${enLettres}`,
        fontSize: 10,
        italics: true,
        margin: [0, 0, 0, 30],
      },

      {
        columns: [
          { text: '', width: '*' },
          { width: 'auto', text: 'Cachet et signature', fontSize: 10, bold: true },
        ],
      },

      ...(facture.statut === 'ANNULEE'
        ? [
            {
              text: 'FACTURE ANNULÉE',
              fontSize: 30,
              bold: true,
              color: '#D0D0D0',
              alignment: 'center' as const,
              margin: [0, 30, 0, 0] as [number, number, number, number],
            },
          ]
        : []),
    ],

    // Coordonnées légales : reprises au caractère près du papier existant.
    footer: () => ({
      stack: [
        {
          canvas: [{ type: 'rect', x: 40, y: 0, w: 515, h: 2.5, color: VERT }],
          margin: [0, 0, 0, 6],
        },
        {
          text: SOCIETE.adresse,
          alignment: 'center',
          fontSize: 7.5,
          color: GRIS,
        },
        {
          text: SOCIETE.legal,
          alignment: 'center',
          fontSize: 7.5,
          color: GRIS,
          margin: [0, 2, 0, 0],
        },
      ],
      margin: [40, 20, 40, 0],
    }),

    defaultStyle: { font: 'Roboto', color: NOIR },
    info: {
      title: `Facture ${facture.reference} — ${SOCIETE.nom}`,
      author: SOCIETE.nom,
    },
  };
}

/** Ouvre la boîte de téléchargement du navigateur. */
export async function telechargerFacture(facture: Facture): Promise<void> {
  const pdfMake = await chargerPdfMake();
  pdfMake.createPdf(construireDocument(facture)).download(`${facture.reference}.pdf`);
}

/** Aperçu dans un nouvel onglet, sans enregistrer. */
export async function apercuFacture(facture: Facture): Promise<void> {
  const pdfMake = await chargerPdfMake();
  pdfMake.createPdf(construireDocument(facture)).open();
}
